import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { View, Animated, Text } from 'react-native';
import { Configuration } from '../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { component } from '../../Decorators/Component';
import { AnimatedTextView } from '../Typography/AnimatedTextView';
import { AnimatedPressable } from '../Buttons/AnimatedPressable';
import { HTMLView } from '../Common/HTMLView';
import { CoreConfig } from '../../../CoreConfig';
import { useAnimation } from '../Animations/AnimationHooks';
import { checkColorType } from '../../../Helpers/Hex/Color';
import { isHTML, objectToStyle } from '../../../Helpers/HTML/HTMLParser';
export const ChoiceItemCheckboxView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.checkbox,
    style(theme, mediaQuery, window, props) {
        var _a, _b, _c, _d, _e, _f;
        return {
            container: {
                opacity: props.disabled ? 0.4 : 1,
                flexDirection: ((_a = props.customize) === null || _a === void 0 ? void 0 : _a.dir) === 'reverse'
                    ? 'row-reverse'
                    : 'row',
                flexWrap: 'wrap',
                alignItems: 'center'
            },
            end: {
                flex: 1,
                paddingEnd: ((_b = props.customize) === null || _b === void 0 ? void 0 : _b.dir) === 'reverse'
                    ? 15
                    : 0,
                paddingStart: ((_c = props.customize) === null || _c === void 0 ? void 0 : _c.dir) === 'reverse'
                    ? 0
                    : 15
            },
            start: {},
            title: {
                fontSize: 16,
                lineHeight: 20
            },
            description: {
                fontSize: 12,
                lineHeight: 16
            },
            btn: {
                height: 20,
                width: 20,
                backgroundColor: 'rgba(17, 85, 204, 0)',
                borderColor: ((_d = theme.colors) === null || _d === void 0 ? void 0 : _d.link_color) || 'rgba(99, 120, 143, 1)',
                alignItems: 'center',
                borderRadius: 4,
                borderStyle: 'solid',
                borderWidth: 2,
                padding: 3,
                justifyContent: 'center',
            },
            btnChecked: {
                backgroundColor: props.light
                    ? '#fff'
                    : ((_e = theme.colors) === null || _e === void 0 ? void 0 : _e.secondary_color) || 'rgba(17, 85, 204, 1)',
                borderColor: props.light
                    ? '#fff'
                    : ((_f = theme.colors) === null || _f === void 0 ? void 0 : _f.secondary_color) || 'rgba(17, 85, 204, 1)',
                backgroundRepeat: 'no-repeat',
            },
            icon: {
                marginTop: 1,
                fontSize: 10,
                lineHeight: 10
            }
        };
    }
})(({ style, value, id, children, testID, onChange, title, description, disabled, light }) => {
    var _a, _b, _c, _d, _e;
    const [color, setColor] = useAnimation({ initialValue: 0, duration: 200, nativeDriver: false });
    const [borderColor, setBorderColor] = useAnimation({ initialValue: 0, duration: 200, nativeDriver: false });
    const handleChange = useCallback(() => {
        if (disabled) {
            return;
        }
        const updatedValue = value === 0
            ? 1
            : 0;
        onChange === null || onChange === void 0 ? void 0 : onChange(id, updatedValue);
    }, [value]);
    useEffect(() => {
        setColor(!!value ? 1 : 0);
        setBorderColor(!!value ? 1 : 0);
    }, [value]);
    const handleMouseOver = useCallback(() => {
        if (disabled) {
            return;
        }
        setBorderColor(1);
    }, [value]);
    const handleMouseLeave = useCallback(() => {
        if (disabled) {
            return;
        }
        !value && setBorderColor(0);
    }, [value]);
    const backgroundColor = color.interpolate({
        inputRange: [0, 1],
        outputRange: [(_a = style === null || style === void 0 ? void 0 : style.btn) === null || _a === void 0 ? void 0 : _a.backgroundColor, (_b = style === null || style === void 0 ? void 0 : style.btnChecked) === null || _b === void 0 ? void 0 : _b.backgroundColor]
    });
    const bColor = borderColor.interpolate({
        inputRange: [0, 1],
        outputRange: [(_c = style === null || style === void 0 ? void 0 : style.btn) === null || _c === void 0 ? void 0 : _c.borderColor, (_d = style === null || style === void 0 ? void 0 : style.btnChecked) === null || _d === void 0 ? void 0 : _d.borderColor]
    });
    const iconColor = useMemo(() => {
        var _a;
        return checkColorType((_a = style === null || style === void 0 ? void 0 : style.btnChecked) === null || _a === void 0 ? void 0 : _a.backgroundColor) === 'dark'
            ? '#63778F'
            : '#fff';
    }, [(_e = style === null || style === void 0 ? void 0 : style.btnChecked) === null || _e === void 0 ? void 0 : _e.backgroundColor]);
    return (_jsxs(AnimatedPressable, { onMouseOver: handleMouseOver,
        onMouseLeave: handleMouseLeave, style: style === null || style === void 0 ? void 0 : style.container, onPress: handleChange, testID: `choice-item-${testID || title || ''}`, children: [_jsx(AnimatedPressable, { testID: `checkbox-${testID || title || ''}`, onPress: handleChange, style: [
                    style === null || style === void 0 ? void 0 : style.start,
                    style === null || style === void 0 ? void 0 : style.btn,
                    !!value && (style === null || style === void 0 ? void 0 : style.btnChecked),
                    { backgroundColor, borderColor: bColor }
                ], children: _jsx(Animated.View, { style: { opacity: color, alignItems: 'center', justifyContent: 'center' }, children: _jsx(Text, { style: [style === null || style === void 0 ? void 0 : style.icon, { color: iconColor }], children: "\u2713" }) }) }), _jsx(View, { style: style === null || style === void 0 ? void 0 : style.end, children: !!children
                    ? children
                    : (_jsxs(_Fragment, { children: [!!title && (_jsx(AnimatedTextView, { textStyle: style === null || style === void 0 ? void 0 : style.title, light: light, children: title })), !!description
                                ? isHTML(description)
                                    ? (_jsx(HTMLView, { value: `<small style="${objectToStyle(style === null || style === void 0 ? void 0 : style.description)}">${description}</small>` }))
                                    : (_jsx(AnimatedTextView, { textStyle: style === null || style === void 0 ? void 0 : style.description, light: light, children: description }))
                                : _jsx(_Fragment, {})] })) })] }));
});
